import React, { useState } from 'react';
import { Eye, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { platformAdminApi } from '../../services/platform-admin-api';

/**
 * Always-visible reminder that the screen belongs to someone else. Rendered
 * above the router so it cannot be scrolled away or hidden by a page layout —
 * an admin who forgets they are impersonating is the whole risk of this feature.
 */
const ImpersonationBanner: React.FC = () => {
  const { impersonation, user } = useAuth();
  const [exiting, setExiting] = useState(false);

  if (!impersonation) return null;

  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();

  const handleExit = async () => {
    setExiting(true);
    try {
      await platformAdminApi.endImpersonation();
    } finally {
      setExiting(false);
    }
  };

  return (
    <div className="sticky top-0 z-50 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-warn/40 bg-warn/15 px-4 py-2 text-[13px] text-foreground">
      <Eye className="h-4 w-4 flex-none text-warn" />
      <span className="min-w-0">
        Viewing as <span className="font-semibold">{name || user?.email}</span>
        {name && user?.email && (
          <span className="text-muted-foreground"> · {user.email}</span>
        )}
      </span>
      <span className="text-muted-foreground">
        Support session
        {impersonation.actorEmail ? ` opened by ${impersonation.actorEmail}` : ''} · expires within
        1 hour
      </span>
      <button
        onClick={handleExit}
        disabled={exiting}
        className="ml-auto inline-flex items-center gap-1.5 rounded-[10px] border border-warn/50 bg-card px-3 py-1 text-[12.5px] font-semibold text-foreground hover:bg-accent disabled:opacity-50"
      >
        <LogOut className="h-3.5 w-3.5" />
        {exiting ? 'Exiting…' : 'Exit'}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
